import type { ReactNode } from 'react'
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined'
import QrCodeScannerOutlinedIcon from '@mui/icons-material/QrCodeScannerOutlined'
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined'
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined'

import { APP_LOGO, APP_NAME } from '../lib/branding'

interface Props {
  title: ReactNode
  subtitle?: ReactNode
  children: ReactNode
  /** Top-bar swap link, e.g. login → register */
  swap?: { prompt: string; cta: string; to: string }
  /** Override the brand-panel headline copy on a per-page basis */
  brand?: { eyebrow: string; headline: ReactNode; tagline: string }
}

const FEATURES = [
  { icon: QrCodeScannerOutlinedIcon,       label: 'One-scan public reporting' },
  { icon: NotificationsActiveOutlinedIcon, label: 'Instant team notifications' },
  { icon: HealthAndSafetyOutlinedIcon,     label: 'Per-user acknowledgements' },
  { icon: VerifiedOutlinedIcon,            label: 'Audit-ready safety trail' },
]

const DEFAULT_BRAND = {
  eyebrow: `${APP_NAME} HSE`,
  headline: (
    <>
      Safety,{' '}
      <Box component="span" sx={{ display: 'inline-block', position: 'relative' }}>
        acknowledged.
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 4,
            height: 10,
            // Underline swipe in the sila-orange accent instead of
            // the previous translucent white, so it reads as a brand
            // highlight rather than a whitewash.
            bgcolor: 'rgba(249,115,22,0.55)',
            borderRadius: 999,
            zIndex: -1,
          }}
        />
      </Box>
    </>
  ),
  tagline:
    'Frontline risk reports, acknowledged the moment they land — every site, every shift, one place.',
}

// Split layout — brand panel left, form panel right. Brand panel uses
// the sila palette: charcoal (the logo letter grey, deepened) as the
// gradient base, with an orange radial glow (the logo dot) top-right
// so the mark echoes across the panel.
export function AuthShell({ title, subtitle, children, swap, brand }: Props) {
  const b = brand ?? DEFAULT_BRAND

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '1.05fr 1fr' },
        bgcolor: 'background.default',
      }}
    >
      {/* ─────────────── BRAND PANEL ─────────────── */}
      <Box
        sx={{
          position: 'relative',
          color: 'common.white',
          // Deep charcoal gradient — matches the sila logo letter
          // colour at the top left and darkens into near-black at the
          // bottom right. The orange orb below adds the accent.
          background:
            'linear-gradient(140deg, #2F3237 0%, #1F2226 45%, #16181B 100%)',
          overflow: 'hidden',
          display: { xs: 'none', lg: 'flex' },
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          p: { lg: 6, xl: 10 },
        }}
      >
        {/* Orange radial glow — top-right — echoes the sila dot. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: -160,
            right: -120,
            width: 520,
            height: 520,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(249,115,22,0.35) 0%, rgba(249,115,22,0) 65%)',
            pointerEvents: 'none',
          }}
        />
        {/* Cool grey wash — bottom-left — cools the composition so it
            doesn't feel monotone charcoal. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            bottom: -220,
            left: -160,
            width: 560,
            height: 560,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(148,163,184,0.18) 0%, rgba(148,163,184,0) 70%)',
            pointerEvents: 'none',
          }}
        />
        {/* Faint noise/dot pattern hint via a very subtle inner ring —
            keeps the panel from looking flat under high-DPI displays. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.04) 0%, transparent 60%)',
            pointerEvents: 'none',
          }}
        />

        {/* Top-left — logo. Absolute-positioned so it doesn't
            participate in the vertical centering below; logo renders
            in native colours (no plate) so the sila brand mark stays
            consistent with the rest of the app. */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ position: 'absolute', top: { lg: 40, xl: 56 }, left: { lg: 48, xl: 64 }, zIndex: 2 }}
        >
          <Box
            component="img"
            src={APP_LOGO}
            alt={`${APP_NAME} logo`}
            sx={{
              height: 30, width: 'auto', maxWidth: 100, objectFit: 'contain', display: 'block',
              // Brand panel is always the dark charcoal gradient — the
              // native grey letters + orange dot disappear against it.
              // `brightness(0) invert(1)` re-colours every pixel to pure
              // white which stays legible without an opaque plate.
              filter: 'brightness(0) invert(1)',
            }}
          />
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: -0.3 }}>
            {APP_NAME}
          </Typography>
        </Stack>

        {/* Hero copy — left-aligned. Vertically centred in the panel
            via the parent's justifyContent, and horizontally anchored
            to the left edge with a comfortable indent so the copy
            reads like a marketing hero rather than a modal centrepiece. */}
        <Stack
          spacing={3.5}
          alignItems="flex-start"
          sx={{
            position: 'relative',
            zIndex: 1,
            maxWidth: 580,
            textAlign: 'left',
          }}
        >
          <Chip
            label={b.eyebrow}
            size="small"
            sx={{
              bgcolor: 'rgba(249,115,22,0.18)',
              color: '#FDBA74',
              fontWeight: 600,
              borderRadius: 999,
              px: 0.5,
              border: '1px solid rgba(249,115,22,0.32)',
              // Subtle orange glow behind the pill to give it lift.
              boxShadow: '0 0 24px rgba(249,115,22,0.15)',
            }}
          />
          <Typography
            variant="h1"
            sx={{
              fontWeight: 800,
              letterSpacing: -1.6,
              fontSize: { lg: 56, xl: 68 },
              lineHeight: 1.02,
            }}
          >
            {b.headline}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255,255,255,0.72)',
              fontSize: 17,
              lineHeight: 1.55,
              maxWidth: 500,
            }}
          >
            {b.tagline}
          </Typography>

          {/* Feature grid — 2×2 premium cards instead of pill chips.
              Icon sits inside an orange-tinted tile so it reads as a
              real product-feature card, not a tag. */}
          <Box
            sx={{
              mt: 1.5,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1.5,
              width: '100%',
              maxWidth: 520,
            }}
          >
            {FEATURES.map((f) => (
              <Stack
                key={f.label}
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{
                  px: 2, py: 1.5,
                  borderRadius: 2.5,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  backdropFilter: 'blur(8px)',
                  transition: 'all 200ms ease',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.08)',
                    borderColor: 'rgba(249,115,22,0.35)',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 36, height: 36,
                    borderRadius: 1.5,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(249,115,22,0.14)',
                    border: '1px solid rgba(249,115,22,0.28)',
                    flexShrink: 0,
                  }}
                >
                  <f.icon sx={{ fontSize: 18, color: '#FDBA74' }} />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13.5, letterSpacing: -0.1 }}>
                  {f.label}
                </Typography>
              </Stack>
            ))}
          </Box>

          {/* Live status ping — subtle "system alive" indicator that
              gives the hero a real-product feel instead of a static
              marketing page. The dot pulses to reinforce it. */}
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{
              mt: 1.5,
              pt: 2.5,
              borderTop: '1px solid rgba(255,255,255,0.08)',
              width: '100%',
              maxWidth: 520,
            }}
          >
            <Box
              sx={{
                width: 8, height: 8,
                borderRadius: 99,
                bgcolor: '#34D399',
                boxShadow: '0 0 0 4px rgba(52,211,153,0.18)',
                animation: 'pulse 2.4s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                  '50%':      { opacity: 0.55, transform: 'scale(0.85)' },
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12.5, fontWeight: 500, letterSpacing: 0.2 }}
            >
              All systems operational · Real-time report intake
            </Typography>
          </Stack>
        </Stack>

      </Box>

      {/* ─────────────── FORM PANEL ─────────────── */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100dvh',
          position: 'relative',
        }}
      >
        {/* Mobile-only top bar (logo) */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{
            display: { xs: 'flex', lg: 'none' },
            px: 3,
            pt: 3,
          }}
        >
          <Box
            component="img"
            src={APP_LOGO}
            alt={`${APP_NAME} logo`}
            sx={{
              height: 28,
              width: 'auto',
              maxWidth: 90,
              objectFit: 'contain',
              display: 'inline-block',
            }}
          />
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: -0.3 }}>
            {APP_NAME}
          </Typography>
        </Stack>

        {/* Top-right swap action */}
        {swap && (
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{
              position: { xs: 'static', lg: 'absolute' },
              top: 32,
              right: 48,
              alignSelf: { xs: 'flex-end', lg: 'auto' },
              px: { xs: 3, lg: 0 },
              pt: { xs: 2, lg: 0 },
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}
            >
              {swap.prompt}
            </Typography>
            <Button
              component={RouterLink}
              to={swap.to}
              variant="outlined"
              size="medium"
              sx={{ borderRadius: 999, px: 3, fontWeight: 600 }}
            >
              {swap.cta}
            </Button>
          </Stack>
        )}

        {/* The form itself, centered */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 3, sm: 6, lg: 8 },
            py: { xs: 4, lg: 8 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: 520,
              p: { xs: 3, sm: 5 },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 4,
              bgcolor: 'background.paper',
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                letterSpacing: -0.8,
                fontSize: { xs: 32, sm: 38 },
                lineHeight: 1.1,
                mb: 1,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body1"
                sx={{ color: 'text.secondary', mb: 4, fontSize: 16 }}
              >
                {subtitle}
              </Typography>
            )}

            {children}
          </Paper>
        </Box>
      </Box>
    </Box>
  )
}
